/* Shared, progressive motion. Authored arrivals and counters run once; content
   is readable before JS and when motion is disabled. No automatic heading tags. */
(function () {
  'use strict';
  if (window.__fizzyRvMotion) return;
  window.__fizzyRvMotion = 1;
  var preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  var active = new Set();
  var counted = new WeakSet();
  var layers = new Set();
  var raf = 0;
  var loops = new Map();
  function quiet() { return preference.matches || document.hidden; }

  function countUp(el) {
    if (counted.has(el)) return;
    counted.add(el);
    var original = el.textContent.trim();
    var match = original.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
    if (!match || quiet()) return;
    var target = Number(match[2].replace(/,/g, ''));
    if (!target) return;
    var decimals = (match[2].split('.')[1] || '').length;
    var formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
      useGrouping: match[2].indexOf(',') !== -1
    });
    var oldWidth = el.style.minWidth;
    el.style.minWidth = el.getBoundingClientRect().width + 'px';
    var accessible = document.createElement('span');
    accessible.className = 'fizzy-count-accessible';
    accessible.textContent = original;
    var visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');
    visual.textContent = original;
    el.replaceChildren(accessible, visual);
    var frame = 0, start = null;
    var delay = Math.min(140, Math.max(0, Number(el.dataset.countDelay) || 0));
    function finish() {
      cancelAnimationFrame(frame);
      el.textContent = original;
      el.style.minWidth = oldWidth;
      active.delete(finish);
    }
    active.add(finish);
    function tick(time) {
      if (!el.isConnected || quiet()) { finish(); return; }
      if (start === null) start = time;
      var progress = Math.max(0, Math.min(1, (time - start - delay) / 700));
      var value = target * (1 - Math.pow(1 - progress, 3));
      visual.textContent = match[1] + formatted.format(value) + match[3];
      if (progress < 1) frame = requestAnimationFrame(tick); else finish();
    }
    frame = requestAnimationFrame(tick);
  }
  function arrive(el) {
    el.classList.add('in-view');
    // Home content is visible on first paint; never hide it again on intersection.
    if (document.body.classList.contains('fizzy-home')) return;
    if (!quiet() && el.animate && !el.hasAttribute('data-rvanim-count')) {
      var delay = parseFloat(getComputedStyle(el).getPropertyValue('--rvanim-delay')) || 0;
      var animation = el.animate([
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'none' }
      ], { duration: 420, delay: Math.min(140, delay), easing: 'cubic-bezier(.16,1,.3,1)' });
      var finish = function () { animation.cancel(); active.delete(finish); };
      active.add(finish);
      animation.onfinish = finish;
    }
  }
  var observer = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      if (entry.target.hasAttribute('data-rvanim')) arrive(entry.target);
      if (entry.target.hasAttribute('data-rvanim-count')) countUp(entry.target);
    });
  }, { threshold: .15 }) : null;

  function applyParallax() {
    raf = 0;
    layers.forEach(function (el) {
      if (!el.isConnected) { layers.delete(el); return; }
      if (quiet()) { el.style.transform = ''; return; }
      var rect = (el.parentElement || el).getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      var shift = Math.max(-30, Math.min(30, (rect.top + rect.height / 2 - innerHeight / 2) * -.05));
      el.style.transform = 'translateY(' + shift.toFixed(1) + 'px) scale(1.08)';
    });
  }
  function scheduleParallax() {
    if (layers.size && !raf) raf = requestAnimationFrame(applyParallax);
  }
  function syncLoops() {
    loops.forEach(function (visible, el) {
      if (!el.isConnected) { if (loopObserver) loopObserver.unobserve(el); loops.delete(el); return; }
      el.toggleAttribute('data-fizzy-loop-paused', quiet() || !visible);
    });
  }
  var loopObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) { loops.set(entry.target, entry.isIntersecting); });
    syncLoops();
  }) : null;
  function init() {
    document.querySelectorAll('[data-rvanim], [data-rvanim-count]').forEach(function (el) {
      if (el.hasAttribute('data-rvanim-bound')) return;
      el.setAttribute('data-rvanim-bound', '1');
      if (observer && !preference.matches) observer.observe(el);
      else { if (el.hasAttribute('data-rvanim')) arrive(el); counted.add(el); }
    });
    document.querySelectorAll('.fizzy-trust-ticker, .fizzy-ingredients').forEach(function (el) {
      if (loops.has(el)) return;
      loops.set(el, !loopObserver);
      if (loopObserver) loopObserver.observe(el);
    });
    syncLoops();
    document.querySelectorAll('[data-rvanim-parallax]').forEach(function (el) { layers.add(el); });
    scheduleParallax();
  }
  function preferenceChanged() {
    syncLoops();
    if (quiet()) Array.from(active).forEach(function (finish) { finish(); });
    scheduleParallax();
  }
  window.addEventListener('scroll', scheduleParallax, { passive: true });
  window.addEventListener('resize', scheduleParallax, { passive: true });
  document.addEventListener('visibilitychange', preferenceChanged);
  if (preference.addEventListener) preference.addEventListener('change', preferenceChanged);
  else preference.addListener(preferenceChanged);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () {
    Array.from(active).forEach(function (finish) { finish(); });
    syncLoops();
    scheduleParallax();
  });
  document.addEventListener('fizzy:motion:refresh', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
